import { useMemo, useCallback, useState } from 'react';
import Grid from '@mui/material/Grid2';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import { abyssColors } from '@/theme/colors';
import { titleCaseFromKey } from '@/utils';

type ReportConfig = {
  key: string;
  title: string;
  visibility?: boolean;
};

type ReportConfigListProps = {
  mode: 'ReadOnly' | 'Edit';
  reportConfig: ReportConfig[];
  columns: string[][];
  enableEditing?: boolean;
  onToggleKey?: (key: string, nextChecked: boolean) => void;
  onUpdateTitle: (key: string, nextTitle: string) => Promise<void> | void;
  maxCheckedFields?: number;
  maxCheckedTooltip?: string;
};

type KeyRowProps = {
  mode: 'ReadOnly' | 'Edit';
  keyName: string;
  existing?: ReportConfig | undefined;
  enableEditing?: boolean;
  checked: boolean;
  isEditing: boolean;
  draftTitle: string;
  onToggle: (keyName: string, next: boolean) => void;
  onStartEdit: (keyName: string) => void;
  onChangeDraft: (v: string) => void;
  onSave: (keyName: string) => void | Promise<void>;
  onCancel: () => void;
  disabled?: boolean;
  disabledTooltip?: string;
};

const KeyRow = ({
  mode,
  keyName,
  existing,
  checked,
  isEditing,
  enableEditing,
  draftTitle,
  onToggle,
  onStartEdit,
  onChangeDraft,
  onSave,
  onCancel,
  disabled = false,
  disabledTooltip,
}: KeyRowProps) => {
  const label = useMemo(() => titleCaseFromKey(keyName), [keyName]);
  const readOnly = mode === 'ReadOnly';
  const isCheckboxDisabled = readOnly || disabled;

  const handleKeyDown = useCallback(
    async (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        await onSave(keyName);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    },
    [keyName, onSave, onCancel]
  );

  const checkbox = (
    <Checkbox
      checked={checked}
      onChange={(e) => onToggle(keyName, e.target.checked)}
      disabled={isCheckboxDisabled}
      inputProps={{ 'aria-label': `Toggle ${label}` }}
    />
  );

  return (
    <Box key={keyName}>
      <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ width: '100%' }}>
        <Box sx={{ pt: 0.5 }}>
          {disabled && !checked && disabledTooltip ? (
            <Tooltip title={disabledTooltip} arrow>
              <span>{checkbox}</span>
            </Tooltip>
          ) : (
            checkbox
          )}
        </Box>
        <Stack sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
            {label}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
              Title:
            </Typography>
            {isEditing && mode === 'Edit' ? (
              <TextField
                variant="standard"
                value={draftTitle}
                onChange={(e) => onChangeDraft(e.target.value)}
                onKeyDown={handleKeyDown}
                autoFocus
                fullWidth
                size="small"
                placeholder="Enter title"
                disabled={readOnly}
                slotProps={{
                  input: {
                    disableUnderline: true,
                    endAdornment: (
                      <>
                        <Tooltip title="Save">
                          <span>
                            <IconButton
                              onClick={() => onSave(keyName)}
                              disabled={readOnly || !draftTitle.trim()}
                              size="small"
                              sx={{ color: abyssColors.primary[500] }}
                            >
                              <CheckIcon sx={{ fontSize: '1.2rem' }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Cancel">
                          <span>
                            <IconButton
                              onClick={onCancel}
                              disabled={readOnly}
                              size="small"
                              sx={{ color: abyssColors.primary[500] }}
                            >
                              <CloseIcon sx={{ fontSize: '1.2rem' }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </>
                    ),
                    sx: {
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      color: abyssColors.primary[500],
                    },
                  },
                }}
              />
            ) : (
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flex: 1 }}>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  {existing?.title ?? '-'}
                </Typography>
                {mode === 'Edit' && enableEditing && (
                  <Tooltip title={checked ? 'Edit title' : 'Enable to edit'}>
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => onStartEdit(keyName)}
                        disabled={readOnly || !checked}
                        sx={{ color: abyssColors.primary[500], p: 0.5 }}
                      >
                        <EditIcon sx={{ fontSize: '1.2rem' }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </Stack>
            )}
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
};

export const ReportConfigList = ({
  mode,
  reportConfig,
  columns,
  enableEditing = true,
  onToggleKey,
  onUpdateTitle,
  maxCheckedFields,
  maxCheckedTooltip,
}: ReportConfigListProps) => {
  const reportConfigByKey = useMemo(
    () => new Map(reportConfig.map((config) => [config.key, config])),
    [reportConfig]
  );

  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [draftTitle, setDraftTitle] = useState<string>('');

  const checkedCount = useMemo(
    () => reportConfig.filter((config) => config.visibility !== false).length,
    [reportConfig]
  );

  const startEdit = useCallback(
    (keyName: string) => {
      const current = reportConfigByKey.get(keyName)?.title ?? '';
      setEditingKey(keyName);
      setDraftTitle(current);
    },
    [reportConfigByKey]
  );

  const cancelEdit = useCallback(() => {
    setEditingKey(null);
    setDraftTitle('');
  }, []);

  const saveEdit = useCallback(
    async (keyName: string) => {
      const trimmed = draftTitle.trim();
      if (!trimmed || !keyName) return;
      await onUpdateTitle(keyName, trimmed);
      setEditingKey(null);
      setDraftTitle('');
    },
    [draftTitle, onUpdateTitle]
  );

  const handleToggle = useCallback(
    (keyName: string, next: boolean) => {
      if (!next && editingKey === keyName) cancelEdit();
      onToggleKey?.(keyName, next);
    },
    [onToggleKey, editingKey, cancelEdit]
  );

  return (
    <Grid container spacing={3}>
      {columns.map((keysForColumn, columnIndex) => (
        <Grid key={columnIndex} size={{ xs: 12, md: 6 }}>
          <Stack spacing={1.5}>
            {keysForColumn.map((keyName, keyIndex) => {
              const existing = reportConfigByKey.get(keyName);
              const checked = existing?.visibility ?? Boolean(existing);
              const isEditing = editingKey === keyName;
              const isDisabled =
                maxCheckedFields !== undefined &&
                checkedCount >= maxCheckedFields &&
                !checked;

              return (
                <>
                  <KeyRow
                    mode={mode}
                    keyName={keyName}
                    existing={existing}
                    checked={checked}
                    isEditing={isEditing}
                    enableEditing={enableEditing}
                    draftTitle={isEditing ? draftTitle : existing?.title ?? ''}
                    onToggle={handleToggle}
                    onStartEdit={startEdit}
                    onChangeDraft={setDraftTitle}
                    onSave={saveEdit}
                    onCancel={cancelEdit}
                    disabled={isDisabled}
                    disabledTooltip={isDisabled ? maxCheckedTooltip : undefined}
                  />
                  {keyIndex < keysForColumn.length - 1 && <Divider sx={{ mt: 1, mb: 0.5 }} />}
                </>
              );
            })}
          </Stack>
        </Grid>
      ))}
    </Grid>
  );
};
